import type { Role } from '@/core/auth/types';
import { isChurchWide } from '@/core/permissions';

export type MenuIcon =
  | 'home'
  | 'user'
  | 'learning'
  | 'calendar'
  | 'support-tickets'
  | 'job-search'
  | 'knowledgebase'
  | 'file'
  | 'ui-kits'
  | 'charts'
  | 'contact'
  | 'social'
  | 'task'
  | 'internationalization'
  | 'board'
  | 'project'
  | 'maps';

export interface MenuLink {
  title: string;
  path: string;
  type: 'link';
  icon?: MenuIcon;
  module?: string;
}

export interface MenuSection {
  title: string;
  icon: MenuIcon;
  type: 'sub' | 'link';
  path?: string;
  children?: MenuLink[];
  module?: string;
}

export interface MenuGroup {
  menutitle: string;
  Items: MenuSection[];
}

export const adminMenu: MenuGroup[] = [
  {
    menutitle: 'Church',
    Items: [
      { title: 'Dashboard', icon: 'home', type: 'link', path: '/admin' },
      {
        title: 'People',
        icon: 'user',
        type: 'sub',
        children: [
          { title: 'Members', type: 'link', path: '/admin/members' },
          { title: 'Logins', type: 'link', path: '/admin/users' },
          { title: 'Human resources', type: 'link', path: '/admin/hr/dashboard' },
        ],
      },
      { title: 'Zonal', icon: 'social', type: 'link', path: '/admin/zonal' },
      { title: 'Departments', icon: 'project', type: 'link', path: '/admin/departments' },
      { title: 'Calendar', icon: 'calendar', type: 'link', path: '/admin/calendar' },
      { title: 'Events', icon: 'calendar', type: 'link', path: '/admin/events' },
      { title: 'Training', icon: 'learning', type: 'link', path: '/admin/training' },
      { title: 'Assets', icon: 'task', type: 'link', path: '/admin/assets' },
      { title: 'Expenses', icon: 'knowledgebase', type: 'link', path: '/admin/expenses' },
      { title: 'Giving', icon: 'charts', type: 'link', path: '/admin/giving' },
      { title: 'Communications', icon: 'support-tickets', type: 'link', path: '/admin/communications' },
      { title: 'Pastoral care', icon: 'contact', type: 'link', path: '/admin/pastoral-care' },
      {
        title: 'Churches',
        icon: 'board',
        type: 'link',
        path: '/admin/organizations',
      },
    ],
  },
  {
    menutitle: 'Settings',
    Items: [
      {
        title: 'Locations',
        icon: 'maps',
        type: 'sub',
        children: [
          { title: 'Local', type: 'link', path: '/admin/settings/locations' },
          { title: 'International', type: 'link', path: '/admin/settings/countries' },
          { title: 'Currencies', type: 'link', path: '/admin/settings/currencies' },
          { title: 'Languages', type: 'link', path: '/admin/settings/languages' },
        ],
      },
      {
        title: 'Communications',
        icon: 'support-tickets',
        type: 'link',
        path: '/admin/settings/communication-providers',
      },
    ],
  },
];

export function getMenuForRole(role: Role | null): MenuGroup[] {
  if (!role) return [];
  if (role === 'super_admin') return adminMenu;
  if (isChurchWide(role)) {
    return [
      {
        menutitle: 'Church',
        Items: adminMenu[0].Items.filter((item) => item.path !== '/admin/organizations'),
      },
    ];
  }
  if (role === 'zonal_leader') {
    return [
      {
        menutitle: 'Zonal',
        Items: [
          { title: 'Dashboard', icon: 'home', type: 'link', path: '/admin' },
          { title: 'Members', icon: 'user', type: 'link', path: '/admin/members' },
          { title: 'Zonal', icon: 'social', type: 'link', path: '/admin/zonal' },
        { title: 'Calendar', icon: 'calendar', type: 'link', path: '/admin/calendar' },
          { title: 'Events', icon: 'calendar', type: 'link', path: '/admin/events' },
          { title: 'Training', icon: 'learning', type: 'link', path: '/admin/training' },
          { title: 'Expenses', icon: 'knowledgebase', type: 'link', path: '/admin/expenses' },
          { title: 'Communications', icon: 'support-tickets', type: 'link', path: '/admin/communications' },
        ],
      },
    ];
  }
  return [
    {
      menutitle: 'Ministry',
      Items: [
        { title: 'Dashboard', icon: 'home', type: 'link', path: '/admin' },
        { title: 'Members', icon: 'user', type: 'link', path: '/admin/members' },
        { title: 'Departments', icon: 'project', type: 'link', path: '/admin/departments' },
        { title: 'Calendar', icon: 'calendar', type: 'link', path: '/admin/calendar' },
        { title: 'Events', icon: 'calendar', type: 'link', path: '/admin/events' },
        { title: 'Training', icon: 'learning', type: 'link', path: '/admin/training' },
        { title: 'Expenses', icon: 'knowledgebase', type: 'link', path: '/admin/expenses' },
        { title: 'Communications', icon: 'support-tickets', type: 'link', path: '/admin/communications' },
      ],
    },
  ];
}

export function filterMenuByModules(
  menu: MenuGroup[],
  modules: string[] | null | undefined,
): MenuGroup[] {
  if (modules == null) return menu;
  const allowed = new Set(modules);
  const allowedModule = (mod?: string) => !mod || allowed.has(mod);

  return menu
    .map((group) => ({
      ...group,
      Items: group.Items.map((section) => {
        if (!allowedModule(section.module)) return null;
        if (!section.children?.length) return section;
        const children = section.children.filter((c) => allowedModule(c.module));
        if (!children.length) return null;
        return { ...section, children };
      }).filter((item): item is MenuSection => item != null),
    }))
    .filter((group) => group.Items.length > 0);
}
